<template>
  <EquipmentRow
    :key="`${cable?.id}-${index}`"
    :data-cy="`equipment-row-${cable?.id}-${index}`"
  >
    <template #left></template>

    <template #default="{ toggle }">
      <AddRemoveButtons
        :disable-add-above="disableAddAbove"
        :disable-add-below="disableAddBelow"
        :disable-remove="disableRemove"
        :hide-add-above="hideAddAbove"
        :hide-add-below="hideAddBelow"
        :hide-remove="hideRemove"
        :data-cy="`add-remove-buttons-${cable?.id}`"
        @add-above="$emit('add-above')"
        @add-below="$emit('add-below')"
        @remove="$emit('remove', cable)"
      >
        <template #middle-content>
          <EquipmentPanel
            class="flex-grow-1"
            :rule-names="[
              'volt_loss',
              'power_cable_kv_rating',
              'power_cable_operating_temp_warning',
              'power_cable_operating_temp',
              'cable_obsolete',
              'cable_obsolete',
              'cable_non_lead',
              'motor_max_amps_cable',
              'mle_clearance_traverse',
              'mle_clearance_set',
              'cable_clearance'
            ]"
            :xref-id="cable?.id"
            :stack="stack"
            :data-cy="`equipment-panel-${cable?.id}`"
            @toggle="toggle"
          >
            <template #header>Cable Type</template>
            <template v-if="cable?.r_cable" #trigger>
              {{ computeCableLabel(cable?.r_cable) }}
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <CableSearchView
        :data-cy="`cable-search-view-${cable?.id}`"
        :is-admin="false"
        @select="handleCableSelect(cable, $event, toggle)"
      />
    </template>
    <template #right>
      <ConverterInput
        :rule-names="['power_cable_length']"
        :xref-id="cable?.id"
        :data-cy="`converter-input-${cable?.id}`"
        :decimal-places="2"
        label="Length"
        :model-value="cable?.length_feet || 0"
        :base-unit="LengthUnits.Feet"
        :model="LargeLengthUnits.options()"
        :disabled="!cable?.r_cable"
        mode="write"
        @update:model-value="
          $emit('update:cables', {
            ...cable,
            id: cable?.id,
            length_feet: $event
          })
        "
      />
    </template>
  </EquipmentRow>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { provide, computed } from "vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { computeCableLabel } from "@/models/equipment/cables/properties";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import CableSearchView from "./CableSearchView.vue";
import type { XrefCables } from "@/composables/forms/formTypes";
import type { Cables } from "gql/api";

const props = defineProps({
  cable: {
    type: Object as PropType<XrefCables[number]>,
    default: null
  },
  index: {
    type: Number,
    default: undefined
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  },
  stack: {
    type: String as PropType<"upper" | "lower" | undefined>,
    default: undefined
  }
});

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "add-above"): void;
  (e: "remove", cable: XrefCables[number]): void;
  (e: "update:cables", cable: XrefCables[number]): void;
}>();

function handleCableSelect(
  xrefCable: XrefCables[number],
  selectedCableEquipment: Cables,
  toggle: () => void
) {
  toggle();
  $emit("update:cables", {
    ...xrefCable,
    id: xrefCable?.id,
    cable_id: selectedCableEquipment.id,
    r_cable: selectedCableEquipment
  });
}
provide(
  "selected-equipment-id",
  computed(() => props.cable?.r_cable?.id)
);
</script>
